<template>
  <div class="stk-mt-4">
    <div
      v-if="allowCommentSubmission"
      class="comment-form"
    >
      <textarea
        :value="newComment"
        class="textarea"
        rows="3"
        :placeholder="commentPlaceholder"
        :disabled="isSubmittingComment"
        @input="$emit('update:new-comment', $event.target.value)"
      />
      <Button
        v-if="showCommentSubmitButton"
        type="primary"
        :disabled="isSubmittingComment || !trimmedComment"
        @click="$emit('submit-comment')"
      >
        <span
          v-if="isSubmittingComment"
          class="button-loading-content"
        >
          <i
            class="fa fa-spinner fa-spin"
            aria-hidden="true"
          />
          <span>Submitting Comment...</span>
        </span>
        <span v-else>{{ commentSubmitButtonText }}</span>
      </Button>
    </div>

    <div
      v-if="allowAttachmentUpload"
      class="attachment-uploader"
    >
      <Dropzone
        class="attachment-dropzone-component"
        :multiple="true"
        :show-list="false"
        :on-change="onDropzoneFilesChange"
      />
      <div
        v-if="pendingAttachmentFiles.length > 0"
        class="pending-attachment-list-wrap"
      >
        <div class="pending-attachment-heading">
          {{ pendingAttachmentHeading }}
        </div>
        <ul class="attachment-list pending-attachment-list">
          <li
            v-for="file in pendingAttachmentFiles"
            class="attachment-item"
          >
            <span>{{ file.name }}</span>
            <button
              class="pending-attachment-remove-button"
              type="button"
              :disabled="isSubmittingAttachment"
              @click="removePendingAttachmentFile(file)"
            >
              Remove
            </button>
          </li>
        </ul>
      </div>
      <div
        v-if="showAttachmentSubmitButton"
        class="attachment-send-row"
      >
        <Button
          type="primary"
          :disabled="isSubmittingAttachment || pendingAttachmentFiles.length === 0"
          @click="$emit('submit-attachment', [...pendingAttachmentFiles])"
        >
          <span
            v-if="isSubmittingAttachment"
            class="button-loading-content"
          >
            <i
              class="fa fa-spinner fa-spin"
              aria-hidden="true"
            />
            <span>Sending Attachments...</span>
          </span>
          <span v-else>{{ attachmentSubmitButtonText }}</span>
        </Button>
      </div>
      <p
        v-if="displayedAttachmentError"
        class="form-error"
      >
        {{ displayedAttachmentError }}
      </p>
    </div>
  </div>
</template>

<script>
import { Button, Dropzone } from '@stakeui';

const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;

export default {
  name: 'CaseCommentAttachmentComposer',
  components: {
    Button,
    Dropzone,
  },
  props: {
    newComment: {
      type: String,
      default: '',
    },
    pendingAttachmentFiles: {
      type: Array,
      default: () => [],
    },
    isSubmittingComment: {
      type: Boolean,
      default: false,
    },
    isSubmittingAttachment: {
      type: Boolean,
      default: false,
    },
    allowCommentSubmission: {
      type: Boolean,
      default: true,
    },
    allowAttachmentUpload: {
      type: Boolean,
      default: true,
    },
    showCommentSubmitButton: {
      type: Boolean,
      default: true,
    },
    showAttachmentSubmitButton: {
      type: Boolean,
      default: true,
    },
    commentPlaceholder: {
      type: String,
      default: 'Add a comment',
    },
    commentSubmitButtonText: {
      type: String,
      default: 'Add Comment',
    },
    attachmentSubmitButtonText: {
      type: String,
      default: 'Send Attachments',
    },
    pendingAttachmentHeading: {
      type: String,
      default: 'Ready to send',
    },
    attachmentError: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localAttachmentError: '',
    };
  },
  computed: {
    trimmedComment() {
      return String(this.newComment || '').trim();
    },
    displayedAttachmentError() {
      const errors = [this.localAttachmentError, this.attachmentError].filter(Boolean);
      return errors.join(' ');
    },
  },
  methods: {
    emitPendingAttachmentFiles(nextFiles) {
      this.$emit('update:pending-attachment-files', nextFiles);
    },
    onDropzoneFilesChange(files) {
      if (this.isSubmittingAttachment) {
        return;
      }
      this.addPendingAttachmentFiles(files || []);
    },
    addPendingAttachmentFiles(fileList) {
      const files = Array.from(fileList || []);
      if (!files.length) {
        return;
      }
      const oversizedFiles = files.filter(file => Number(file?.size || 0) > MAX_ATTACHMENT_SIZE_BYTES);
      const acceptedFiles = files.filter(file => Number(file?.size || 0) <= MAX_ATTACHMENT_SIZE_BYTES);
      if (oversizedFiles.length > 0) {
        const oversizedNames = oversizedFiles.map(file => file.name).join(', ');
        this.localAttachmentError = `Attachments must be 25MB or smaller. Oversized file(s): ${oversizedNames}.`;
      } else {
        this.localAttachmentError = '';
      }
      if (!acceptedFiles.length) {
        return;
      }
      const existing = new Set(
          this.pendingAttachmentFiles.map(file => `${file.name}:${file.size}:${file.lastModified}`),
      );
      const nextFiles = acceptedFiles.filter(file => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (existing.has(key)) {
          return false;
        }
        existing.add(key);
        return true;
      });
      this.emitPendingAttachmentFiles([...this.pendingAttachmentFiles, ...nextFiles]);
    },
    removePendingAttachmentFile(targetFile) {
      if (this.isSubmittingAttachment || !targetFile) {
        return;
      }
      const targetFileKey = `${targetFile.name}:${targetFile.size}:${targetFile.lastModified}`;
      const nextFiles = this.pendingAttachmentFiles.filter(file =>
        `${file.name}:${file.size}:${file.lastModified}` !== targetFileKey,
      );
      this.emitPendingAttachmentFiles(nextFiles);
      if (!nextFiles.length) {
        this.localAttachmentError = '';
      }
    },
  },
};
</script>

<style scoped>
.comment-form {
  display: flex;
  flex-direction: column;
  gap: var(--stk-size-3);
}

.textarea {
  border-radius: 0.75rem;
}

.button-loading-content {
  display: inline-flex;
  align-items: center;
  gap: var(--stk-size-2);
}

.attachment-uploader {
  margin-top: var(--stk-size-3);
}

.attachment-dropzone-component {
  margin-bottom: var(--stk-size-3);
}

.attachment-list {
  list-style: none;
  margin: 0 0 var(--stk-size-3) 0;
  padding: 0;
}

.attachment-item {
  display: flex;
  gap: var(--stk-size-2);
  align-items: center;
  margin-bottom: var(--stk-size-2);
}

.pending-attachment-remove-button {
  appearance: none;
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
  background: var(--stk-color-white);
  color: var(--stk-color-text-muted);
  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1.2;
  padding: 0.1rem 0.4rem;
}

.pending-attachment-remove-button:hover:not(:disabled) {
  border-color: var(--stk-color-danger);
  color: var(--stk-color-danger);
}

.pending-attachment-remove-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pending-attachment-list-wrap {
  margin-bottom: var(--stk-size-3);
}

.pending-attachment-heading {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--stk-color-text-muted);
  margin-bottom: var(--stk-size-1);
}

.pending-attachment-list {
  margin-bottom: 0;
}

.attachment-send-row {
  display: flex;
  margin-bottom: var(--stk-size-3);
}

.form-error {
  color: var(--stk-color-danger);
}
</style>
